import React, { useState, useEffect } from 'react';
import { AlertCircle, Clock, Eye, EyeOff, User, Mail, Lock, UserPlus, ArrowLeft, CheckCircle, Building2, X } from 'lucide-react';

const LoginForm = ({ onLogin, logo }) => {
  const [mode, setMode] = useState('login'); // 'login', 'register', or 'forgot-password'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
    schoolIds: []
  });
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  // Fetch schools when switching to register mode
  useEffect(() => {
    if (mode === 'register' && schools.length === 0) {
      fetchSchools();
    }
  }, [mode]);

  const fetchSchools = async () => {
    setLoadingSchools(true);
    try {
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/schools/public`;
      console.log('Fetching schools from:', url);
      const response = await fetch(url);
      console.log('Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Schools fetched:', data);
        setSchools(data);
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch schools:', errorData);
        setError('Failed to load schools. Please try again.');
      }
    } catch (error) {
      console.error('Failed to fetch schools:', error);
      setError('Failed to load schools. Please refresh the page.');
    } finally {
      setLoadingSchools(false);
    }
  };

  const validateForm = () => {
    if (mode === 'forgot-password') {
      if (!forgotPasswordEmail) {
        setError('Email address is required');
        return false;
      }
      return true;
    }

    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      return false;
    }

    if (mode === 'register') {
      if (!formData.firstName || !formData.lastName) {
        setError('First name and last name are required');
        return false;
      }

      if (!formData.schoolIds || formData.schoolIds.length === 0) {
        setError('Please select at least one school');
        return false;
      }

      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters long');
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let endpoint, payload;
      
      if (mode === 'forgot-password') {
        endpoint = '/api/auth/forgot-password';
        payload = { email: forgotPasswordEmail };
      } else {
        endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
        payload = mode === 'login'
          ? { email: formData.email, password: formData.password }
          : {
              email: formData.email,
              password: formData.password,
              firstName: formData.firstName,
              lastName: formData.lastName,
              schoolIds: formData.schoolIds
            };
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        if (mode === 'forgot-password') {
          setSuccess(data.message);
          // Optionally switch back to login after a delay
          setTimeout(() => {
            setMode('login');
            setSuccess('');
          }, 5000);
        } else {
          onLogin(data.user, data.token);
        }
      } else {
        setError(data.error || `${mode === 'login' ? 'Login' : mode === 'register' ? 'Registration' : 'Password reset request'} failed`);
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
    setForgotPasswordEmail('');
    
    // Clear registration-specific fields when switching away from register
    if (mode === 'register' && newMode !== 'register') {
      setFormData(prev => ({
        ...prev,
        firstName: '',
        lastName: '',
        confirmPassword: '',
        schoolIds: []
      }));
    }
  };

  const getModeTitle = () => {
    switch (mode) {
      case 'login': return 'Sign in to your account';
      case 'register': return 'Create a new account';
      case 'forgot-password': return 'Reset your password';
      default: return 'Sign in to your account';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            {logo || <Clock className="w-12 h-12 text-blue-500" />}
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Volunteer Time Tracker</h1>
          <p className="text-gray-600 mt-2">El Segundo School District</p>
          <p className="text-gray-500 text-sm mt-1">
            {getModeTitle()}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-700 border border-green-200 rounded-lg p-3 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'forgot-password' ? (
            /* Forgot Password Form */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your email address"
                  required
                />
                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                We'll send you a link to reset your password
              </p>
            </div>
          ) : (
            /* Login/Register Form */
            <>
              {mode === 'register' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="John"
                        required
                      />
                      <User className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Doe"
                        required
                      />
                      <User className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                    required
                  />
                  <Mail className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {mode === 'register' && (
                  <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
                )}
              </div>

              {mode === 'register' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="••••••••"
                        required
                      />
                      <Lock className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      School *
                    </label>
                    {loadingSchools ? (
                      <div className="flex items-center justify-center py-3 text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent mr-2"></div>
                        Loading schools...
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <select
                          onChange={(e) => {
                            const schoolId = e.target.value;
                            if (schoolId && !formData.schoolIds.includes(schoolId)) {
                              handleInputChange('schoolIds', [...formData.schoolIds, schoolId]);
                            }
                            e.target.value = '';
                          }}
                          className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">
                            {formData.schoolIds.length === 0
                              ? 'Select your school...'
                              : 'Add another school...'}
                          </option>
                          {schools
                            .filter(school => !formData.schoolIds.includes(school.id))
                            .map(school => (
                              <option key={school.id} value={school.id}>
                                {school.name}
                              </option>
                            ))}
                        </select>

                        {formData.schoolIds.length > 0 && (
                          <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            {formData.schoolIds.map(schoolId => {
                              const school = schools.find(s => s.id === schoolId);
                              return school ? (
                                <div
                                  key={schoolId}
                                  className="flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                                >
                                  <Building2 className="w-3 h-3" />
                                  <span className="font-medium">{school.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleInputChange(
                                        'schoolIds',
                                        formData.schoolIds.filter(id => id !== schoolId)
                                      );
                                    }}
                                    className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            ) : mode === 'login' ? (
              <Clock className="w-4 h-4" />
            ) : mode === 'register' ? (
              <UserPlus className="w-4 h-4" />
            ) : (
              <Mail className="w-4 h-4" />
            )}
            <span>
              {loading 
                ? (mode === 'login' ? 'Signing in...' : mode === 'register' ? 'Creating account...' : 'Sending reset link...') 
                : (mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Send Reset Link')
              }
            </span>
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          {mode === 'forgot-password' ? (
            <button
              onClick={() => switchMode('login')}
              disabled={loading}
              className="flex items-center justify-center w-full text-blue-600 hover:text-blue-800 text-sm font-medium disabled:text-gray-400"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Sign In
            </button>
          ) : (
            <>
              <button
                onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                disabled={loading}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:text-gray-400"
              >
                {mode === 'login' 
                  ? "Don't have an account? Create one" 
                  : "Already have an account? Sign in"
                }
              </button>
              
              {mode === 'login' && (
                <div>
                  <button
                    onClick={() => switchMode('forgot-password')}
                    disabled={loading}
                    className="text-gray-600 hover:text-blue-600 text-sm disabled:text-gray-400"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {mode === 'login' && (
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Support:</p>
            <p>Email: RSSPTAEvents@gmail.com</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;